import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  archiveProject,
  deleteProject,
  fetchMilestonesOnce,
  setMilestoneStatus,
  subscribeAllProjects,
  subscribeAllUsers,
  unarchiveProject,
  updateProject,
} from "../../projects/api";
import { listAccounts } from "../../lib/accounts";
import { PROJECT_STATUSES } from "../../projects/pricing";
import { Card, StageChip, Stat, Empty } from "../../dashboard/primitives";
import NewProjectForm from "./NewProjectForm";
import SubscriptionPanel from "../../components/SubscriptionPanel";
import { Skeleton, SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const MILESTONE_STATUSES = ["pending", "invoiced", "paid"];
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const MilestonesPanel = ({ projectId }) => {
  const [milestones, setMilestones] = useState(null);
  useEffect(() => {
    let live = true;
    fetchMilestonesOnce(projectId).then((rows) => { if (live) setMilestones(rows); });
    return () => { live = false; };
  }, [projectId]);

  if (milestones === null) return (
    <div style={{ display: "grid", gap: 8 }}>
      <Skeleton width="70%" height={14} />
      <Skeleton width="50%" height={14} />
      <Skeleton width="60%" height={14} />
    </div>
  );
  if (milestones.length === 0) return <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>No milestones</div>;

  return (
    <div>
      {milestones.map((m) => (
        <div key={m.id} className="fw-stage-row">
          <span className="fw-stage-row__idx">{String((m.order ?? 0) + 1).padStart(2, "0")}</span>
          <span>{m.label} · <span style={{ color: "var(--fw-muted)" }}>{fmt(m.amount)}</span></span>
          <StageChip status={m.status === "paid" ? "paid" : m.status === "invoiced" ? "awaiting_approval" : "pending"} />
          <select
            className="fw-select"
            style={{ width: "auto", fontSize: 12, padding: "4px 8px" }}
            value={m.status}
            onChange={(e) => setMilestoneStatus(projectId, m.id, e.target.value)}
          >
            {MILESTONE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
};

const ProjectsSection = () => {
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [subPanelId, setSubPanelId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(
    () =>
      subscribeAllProjects((v) => {
        setProjects(v);
        setProjectsLoaded(true);
      }),
    []
  );
  useEffect(() => subscribeAllUsers(setUsers), []);
  useEffect(() => {
    listAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  const showSkeleton = useMinLoading(!projectsLoaded);

  const usersByUid = useMemo(
    () => Object.fromEntries(users.map((c) => [c.id, c])),
    [users]
  );
  const accountsById = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a])),
    [accounts]
  );

  const visibleProjects = useMemo(
    () => projects.filter((p) => (showArchived ? p.archived : !p.archived)),
    [projects, showArchived]
  );

  const archivedCount = useMemo(
    () => projects.filter((p) => p.archived).length,
    [projects]
  );

  const stats = useMemo(() => {
    const live = projects.filter((p) => !p.archived);
    const active = live.filter((p) => !["launched", "on-hold"].includes(p.status)).length;
    const launched = live.filter((p) => p.status === "launched").length;
    const totalBudget = live.reduce((n, p) => n + (p.budgetUsd || 0), 0);
    return { total: live.length, active, launched, totalBudget };
  }, [projects]);

  const handleArchive = (p) => {
    if (p.archived) return unarchiveProject(p.id);
    if (window.confirm(`Archive "${p.title}"? It will be hidden from the default list.`)) {
      return archiveProject(p.id);
    }
  };

  const handleDelete = (p) => {
    const ok = window.confirm(
      `Delete "${p.title}" permanently? This removes the project and all milestones, updates, and messages. This cannot be undone.`
    );
    if (!ok) return;
    const confirmTitle = window.prompt(`Type the project title to confirm deletion:\n\n${p.title}`);
    if (confirmTitle !== p.title) return;
    return deleteProject(p.id);
  };

  return (
    <>
      <div className="fw-stat-grid">
        <Stat label="Projects" value={stats.total} loading={showSkeleton} />
        <Stat label="Active" value={stats.active} loading={showSkeleton} />
        <Stat label="Launched" value={stats.launched} loading={showSkeleton} />
        <Stat label="Total budget" value={fmt(stats.totalBudget)} loading={showSkeleton} />
      </div>

      <Card
        title={showArchived ? `Archived projects (${archivedCount})` : "All projects"}
        action={
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="fw-btn fw-btn--ghost"
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? "Show active" : `Show archived${archivedCount ? ` (${archivedCount})` : ""}`}
            </button>
            <button className="fw-btn fw-btn--primary" onClick={() => setShowForm((v) => !v)}>
              {showForm ? "Close" : "New project"}
            </button>
          </div>
        }
      >
        {showForm && <NewProjectForm onCreated={() => setShowForm(false)} />}

        {showSkeleton ? (
          <SkeletonList count={3} ariaLabel="Loading projects">
            <SkeletonRow cols={4} widths={["1fr", "80px", "100px", "240px"]} />
          </SkeletonList>
        ) : visibleProjects.length === 0 ? (
          <Empty>{showArchived ? "No archived projects" : "No projects yet"}</Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {visibleProjects.map((p) => {
              const account = accountsById[p.accountId];
              const owner = usersByUid[p.primaryContactUid || account?.ownerUid];
              const expanded = expandedId === p.id;
              return (
                <div key={p.id} style={{ border: "1px solid var(--fw-line)", borderRadius: "var(--fw-r-md)", padding: 16 }}>
                  <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{p.title}</div>
                      <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)", textTransform: "uppercase", letterSpacing: 1.2, marginTop: 2 }}>
                        {account?.name || p.accountId?.slice(0, 8) || "no account"} · {p.type || "—"} · {fmt(p.budgetUsd)} · {p.paymentPlan}
                      </div>
                    </div>
                    <StageChip status={p.status === "launched" ? "paid" : p.status === "on-hold" ? "closed" : "in_progress"} />
                    <select
                      className="fw-select"
                      style={{ width: "auto", fontSize: 12, padding: "4px 8px" }}
                      value={p.status}
                      onChange={(e) => updateProject(p.id, { status: e.target.value })}
                    >
                      {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button
                        className="fw-btn fw-btn--ghost"
                        style={{ padding: "6px 10px" }}
                        onClick={() => setExpandedId(expanded ? null : p.id)}
                      >
                        {expanded ? "Hide" : "Milestones"}
                      </button>
                      {(p.paymentPlan === "lease" || p.paymentPlan === "rent-to-own") && (
                        <button
                          className="fw-btn fw-btn--ghost"
                          style={{ padding: "6px 10px" }}
                          onClick={() => setSubPanelId(subPanelId === p.id ? null : p.id)}
                        >
                          {subPanelId === p.id ? "Hide sub" : "Subscription"}
                        </button>
                      )}
                      <Link
                        to={`/admin/projects/${p.id}/tasks`}
                        className="fw-btn fw-btn--ghost"
                        style={{ padding: "6px 10px" }}
                      >
                        Tasks →
                      </Link>
                      <button
                        className="fw-btn fw-btn--ghost"
                        style={{ padding: "6px 10px" }}
                        onClick={() => handleArchive(p)}
                      >
                        {p.archived ? "Unarchive" : "Archive"}
                      </button>
                      <button
                        className="fw-btn fw-btn--ghost"
                        style={{ padding: "6px 10px", color: "#c0392b" }}
                        onClick={() => handleDelete(p)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {p.summary && (
                    <p style={{ color: "var(--fw-muted)", fontSize: 13, margin: "12px 0 0" }}>{p.summary}</p>
                  )}
                  {expanded && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--fw-line)" }}>
                      <MilestonesPanel projectId={p.id} />
                    </div>
                  )}
                  {subPanelId === p.id && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--fw-line)" }}>
                      <SubscriptionPanel project={p} owner={owner} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default ProjectsSection;
